import React from 'react';
import Image from '@/components/AppImage/AppImage';
import './OnboardingRoleSelect.css';

export type OnboardingRoleKey = 'regular' | 'creator' | 'advisor';

export interface OnboardingRoleOption {
  key: OnboardingRoleKey;
  label: string;
  headline: string;
  description: string;
  illustration: string;
}

export const ONBOARDING_ROLE_OPTIONS: OnboardingRoleOption[] = [
  {
    key: 'regular',
    label: 'Traveler',
    headline: 'Plan my own trips',
    description: 'Save and organize travel inspiration',
    illustration: '/welcome/onboarding/role-traveler.png',
  },
  {
    key: 'creator',
    label: 'Creator',
    headline: 'Turn my content into trips',
    description: 'Monetize your recommendations',
    illustration: '/welcome/onboarding/role-creator.png',
  },
  {
    key: 'advisor',
    label: 'Travel Advisor',
    headline: 'Create trips for clients',
    description: 'Build trips and grow your business.',
    illustration: '/welcome/onboarding/role-advisor.png',
  },
];

export interface OnboardingRoleSelectProps {
  /** Last chosen role, if any — shown as selected when the user returns to this screen. */
  selectedRole: OnboardingRoleKey | null;
  /** Tapping a card selects the role and immediately continues to the next screen. */
  onSelectRole: (role: OnboardingRoleKey) => void;
  userFirstName?: string;
}

/** First token of display name; ignores backend placeholder "Anonymous". */
export function resolveOnboardingGreetingName(name?: string | null): string {
  const firstName = name?.trim().split(/\s+/)[0]?.trim();
  if (firstName && firstName.toLowerCase() !== 'anonymous') {
    return firstName;
  }
  return 'there';
}

const OnboardingRoleSelect: React.FC<OnboardingRoleSelectProps> = ({
  selectedRole,
  onSelectRole,
  userFirstName,
}) => {
  const greetingName = resolveOnboardingGreetingName(userFirstName);

  return (
    <div className="onboarding-role-select" data-testid="onboarding-role-select">
      <div className="onboarding-role-select__scroll gt-keyboard-scroll">
        <div className="onboarding-role-select__header">
          <p className="onboarding-role-select__greeting">Hi {greetingName}</p>
          <h2 className="onboarding-role-select__title">
            How do you want to use Globe Thrivers?
          </h2>
          <p className="onboarding-role-select__subtitle">
            Pick what fits best for now — you can change it before you finish setup.
          </p>
        </div>

        <div className="onboarding-role-select__cards">
          {ONBOARDING_ROLE_OPTIONS.map((role) => {
            const isSelected = selectedRole === role.key;
            return (
              <button
                key={role.key}
                type="button"
                className={`onboarding-role-select__card ${
                  isSelected ? 'onboarding-role-select__card--selected' : ''
                }`}
                onClick={() => onSelectRole(role.key)}
                data-testid={`onboarding-role-card-${role.key}`}
                data-selected={isSelected}
              >
                <div className="onboarding-role-select__illustration">
                  <Image
                    src={role.illustration}
                    alt=""
                    width={72}
                    height={72}
                    className="onboarding-role-select__illustration-img"
                  />
                </div>
                <div className="onboarding-role-select__card-body">
                  <p
                    className={`onboarding-role-select__role-label ${
                      isSelected
                        ? 'onboarding-role-select__role-label--selected'
                        : 'onboarding-role-select__role-label--unselected'
                    }`}
                  >
                    {role.label}
                  </p>
                  <p
                    className={`onboarding-role-select__headline ${
                      isSelected ? 'onboarding-role-select__headline--selected' : ''
                    }`}
                  >
                    {role.headline}
                  </p>
                  <p className="onboarding-role-select__description">{role.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        <p className="onboarding-role-select__helper" data-testid="onboarding-role-helper">
          Not sure? <strong>Traveler</strong> is a great place to start.
        </p>
      </div>
    </div>
  );
};

export default OnboardingRoleSelect;
